import { useSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import { DownloadOutlined } from '@mui/icons-material';
import {
  Stack,
  Alert,
  Dialog,
  Button,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  DialogTitle,
  FormControl,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { OrgResourceType } from 'src/services/organisation/organisation.types';
import { useExportOrgResourcesMutation } from 'src/services/organisation/organisation.service';

type Props = {
  organizationId: string;
  open: boolean;
  onClose: VoidFunction;
};

export default function ResourceExporterModal({ organizationId, open, onClose }: Props) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const [exportOrgResources, { isLoading }] = useExportOrgResourcesMutation();

  const [selectedResourceType, setSelectedResourceType] = useState<OrgResourceType | ''>('');
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setSelectedResourceType('');
    setError(null);
    onClose();
  }, [onClose]);

  const handleExport = useCallback(async () => {
    if (!selectedResourceType) {
      setError(t('system.resource_exporter.errors.no_resource_selected'));
      return;
    }

    setError(null);

    try {
      const response = await exportOrgResources({
        organizationId,
        resourceType: selectedResourceType,
      }).unwrap();

      // Create a blob URL and trigger download
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `organization-${organizationId}-export-${selectedResourceType}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(t('system.resource_exporter.export_success'), { variant: 'success' });
      handleClose();
    } catch (err: any) {
      console.error('Export error:', err);
      const errorMessage = err?.data?.message || t('system.resource_exporter.errors.export_failed');
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [selectedResourceType, exportOrgResources, organizationId, t, enqueueSnackbar, handleClose]);

  const resourceTypeOptions = [
    { value: OrgResourceType.CONTACTS, label: t('system.resource_exporter.resource_types.contacts') },
    { value: OrgResourceType.COMPANIES, label: t('system.resource_exporter.resource_types.companies') },
    { value: OrgResourceType.CANDIDATES, label: t('system.resource_exporter.resource_types.candidates') },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DownloadOutlined />
          <Typography>{t('system.resource_exporter.title')}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Alert severity="info">
            {t('system.resource_exporter.description')}
          </Alert>

          <FormControl fullWidth>
            <InputLabel id="resource-type-label">
              {t('system.resource_exporter.resource_type_label')}
            </InputLabel>
            <Select
              labelId="resource-type-label"
              id="resource-type-select"
              value={selectedResourceType}
              label={t('system.resource_exporter.resource_type_label')}
              onChange={(e) => setSelectedResourceType(e.target.value as OrgResourceType)}
            >
              {resourceTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleExport}
          loading={isLoading}
          disabled={!selectedResourceType}
          startIcon={<DownloadOutlined />}
        >
          {t('system.resource_exporter.export_button')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}